import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { store } from '../../store';
import { router, type RouteName } from '../../router';
import { api } from '../../api';
import { t } from '../../i18n';
import { icon } from '../ui/icons';

interface PortalNavItem { name: RouteName; path: string; labelKey: string; feature?: string; }

const ITEMS: PortalNavItem[] = [
  { name: 'portal-dashboard', path: '/portal/dashboard', labelKey: 'common.portalNav.dashboard' },
  { name: 'portal-classes', path: '/portal/classes', labelKey: 'common.portalNav.classes' },
  { name: 'portal-levels', path: '/portal/levels', labelKey: 'common.portalNav.levels' },
  { name: 'portal-payments', path: '/portal/payments', labelKey: 'common.portalNav.payments' },
  { name: 'portal-events', path: '/portal/events', labelKey: 'common.portalNav.events', feature: 'feature_events' },
  { name: 'portal-calendar', path: '/portal/calendar', labelKey: 'common.portalNav.calendar', feature: 'feature_calendar' },
  { name: 'portal-gallery', path: '/portal/gallery', labelKey: 'common.portalNav.gallery', feature: 'feature_media' },
  { name: 'portal-profile', path: '/portal/profile', labelKey: 'common.portalNav.profile' },
  { name: 'portal-contract', path: '/portal/contract', labelKey: 'common.portalNav.contract' },
];

/** <cadence-portal-nav> — the student portal's top navigation bar. */
@customElement('cadence-portal-nav')
export class CadencePortalNav extends LitElement {
  @property() active: RouteName = 'portal-dashboard';
  @state() private version = 0;
  @state() private menuOpen = false;
  private unsub?: () => void;

  static styles = css`
    header {
      display: flex; align-items: center; gap: var(--space-4);
      padding: 0 var(--space-5); height: var(--header-height);
      background: var(--color-bg-primary); border-bottom: 1px solid var(--color-border);
      position: sticky; top: 0; z-index: 30;
    }
    .brand { display: flex; align-items: center; gap: 10px; font-weight: 700; flex-shrink: 0; max-width: 240px; }
    .brand .dot { width: 12px; height: 12px; border-radius: 50%; background: var(--color-primary); flex-shrink: 0; }
    .brand .logo { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; background: #fff; flex-shrink: 0; }
    /* Wrap a long school name to at most 2 lines within a capped width instead of
       pushing the nav; a tooltip carries the rest in the rare 3+ line case. */
    .brand .name {
      min-width: 0; font-size: 1rem; line-height: 1.15;
      display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
      overflow: hidden; overflow-wrap: anywhere;
    }
    nav { display: flex; gap: 4px; flex: 1; overflow-x: auto; }
    a {
      padding: 8px 12px; border-radius: var(--radius-md); text-decoration: none; white-space: nowrap;
      color: var(--color-text-secondary); font-weight: 500; font-size: 0.9rem;
    }
    a:hover { background: var(--color-bg-secondary); color: var(--color-text-primary); }
    a[aria-current='page'] { background: color-mix(in srgb, var(--color-primary) 14%, transparent); color: var(--color-primary); }
    .right { display: flex; align-items: center; gap: 10px; }
    .avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--color-primary); color: #fff; display: grid; place-items: center; font-weight: 700; font-size: 0.85rem; }
    .icon-btn { background: none; border: 1px solid var(--color-border); cursor: pointer; color: var(--color-text-secondary); border-radius: var(--radius-md); width: 38px; height: 38px; display: inline-grid; place-items: center; }
    .icon-btn:hover { border-color: var(--color-primary); color: var(--color-primary); }
    /* Declared after .icon-btn so it wins on desktop (equal specificity); the mobile
       media query below re-shows it. */
    .hamburger { display: none; }
    svg { width: 20px; height: 20px; }
    .backdrop { display: none; }

    /* Tablet & mobile: collapse the inline nav into a hamburger dropdown so the nine
       links don't overflow into an unusable horizontal scroll. (The inline bar only
       fits all labels on wide desktops.) */
    @media (max-width: 1024px) {
      .hamburger { display: inline-grid; }
      .right { margin-left: auto; }
      nav {
        position: absolute; top: 100%; left: 0; right: 0; z-index: 25;
        flex-direction: column; gap: 2px; overflow: visible;
        padding: var(--space-3);
        background: var(--color-bg-primary);
        border-bottom: 1px solid var(--color-border);
        box-shadow: var(--shadow-lg);
      }
      nav:not(.open) { display: none; }
      nav a { padding: 12px 14px; font-size: 0.95rem; }
      .backdrop.show { display: block; position: fixed; inset: 0; z-index: 20; }
    }
    /* Only hide the school name on true phones — tablets have room beside the menu. */
    @media (max-width: 640px) { .brand .name { display: none; } }
  `;

  connectedCallback(): void { super.connectedCallback(); this.unsub = store.subscribe(() => this.version++); }
  disconnectedCallback(): void { super.disconnectedCallback(); this.unsub?.(); }

  private go(e: Event, item: PortalNavItem) {
    e.preventDefault();
    this.menuOpen = false;
    router.navigate(item.path);
  }

  private async toggleTheme() {
    const dark = !store.get().darkMode;
    store.applyTheme(dark);
    try { await api.patch('/auth/me', { dark_mode: dark }); } catch { /* applied locally */ }
  }

  render() {
    const { darkMode } = store.get();
    const active: RouteName = this.active === 'portal-event-detail' ? 'portal-events' : this.active;
    return html`
      <header>
        <button
          class="icon-btn hamburger"
          aria-label=${t('common.actions.openMenu')}
          aria-expanded=${this.menuOpen ? 'true' : 'false'}
          @click=${() => (this.menuOpen = !this.menuOpen)}
        >
          ${this.menuOpen ? icon('x', 20) : icon('menu', 20)}
        </button>
        <div class="brand">
          ${store.get().school?.logo_url
            ? html`<img class="logo" src=${store.get().school!.logo_url!} alt="" />`
            : html`<span class="dot"></span>`}
          <span class="name" title=${store.get().school?.name || t('common.app.name')}>${store.get().school?.name || t('common.app.name')}</span>
        </div>
        <div class="backdrop ${this.menuOpen ? 'show' : ''}" @click=${() => (this.menuOpen = false)}></div>
        <nav class=${this.menuOpen ? 'open' : ''} aria-label=${t('common.a11y.portalNav')}>
          ${ITEMS.filter((item) => !item.feature || store.featureEnabled(item.feature)).map((item) => html`
            <a href=${item.path} aria-current=${active === item.name ? 'page' : 'false'} @click=${(e: Event) => this.go(e, item)}>
              ${t(item.labelKey)}
            </a>`)}
        </nav>
        <div class="right">
          <cadence-notification-bell></cadence-notification-bell>
          <cadence-lang-menu></cadence-lang-menu>
          <button class="icon-btn" aria-label=${t('common.actions.toggleTheme')} @click=${() => this.toggleTheme()}>
            ${darkMode ? icon('sun', 20) : icon('moon', 20)}
          </button>
          <cadence-user-menu logout-path="/portal"></cadence-user-menu>
        </div>
      </header>
    `;
  }
}
